import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { MessageSubType } from "@expert/shared-types";

import type { ExpertAssistMessage, SessionSummaryMetadata } from "../shared-types";

import { type FailureReason, failureMessages } from "./failureMessages";
import { getSeparatorLabel, type SeparatorProps } from "./separatorMessages";
import { useTimelineStore } from "./timelineStore";

/**
 * This is intended to set the default messages available to the timeline in state
 * Instead of just using a plain hardcoded list, this allows us to try to pull a
 * list from contentful and use this to store it for use in the timeline
 */
export const setDefaultMessages = (defaultMessages: ExpertAssistMessage[]) => {
    useTimelineStore.getState().setDefaultMessages(defaultMessages);
};
/**
 * This is intended to add regular message to the timeline
 * Please use `addFailureMessage` for specific failures
 */
export const addMessage = (sessionId: string, message: ExpertAssistMessage) => {
    useTimelineStore.getState().addMessage(sessionId, message);
};

export const addMessages = (sessionId: string, messages: ExpertAssistMessage[]) => {
    useTimelineStore.getState().addMessages(sessionId, messages);
};

/**
 * This is intended to add failure message to the timeline
 * Return the ExpertAssistMessage for the added message
 */
export const addFailureMessage = (sessionId: string, failure: FailureReason): ExpertAssistMessage => {
    void sendErrorMetric(`expertAssist.${failure}`);
    const message: ExpertAssistMessage = {
        ...failureMessages[failure],
        id: `botFailureMessage_${crypto.randomUUID()}`,
        isUnread: true,
        status: "active",
    };
    useTimelineStore.getState().addMessage(sessionId, message);
    return message;
};

export const addSeparatorMessage = (sessionId: string, { text, timestamp, callDuration }: SeparatorProps) => {
    const message: ExpertAssistMessage = {
        type: "Separator",
        id: `separator_${crypto.randomUUID()}`,
        text: getSeparatorLabel({ text, timestamp, callDuration }),
        timestamp,
        callDuration,
        isUnread: false,
        status: "active",
    };
    useTimelineStore.getState().addMessage(sessionId, message);
};

export const addSessionSummaryMessage = (
    sessionId: string,
    { header, summary, timestamp, resolutionStatus, sessionType, previousSessionId }: SessionSummaryMetadata,
) => {
    const { addMessage } = useTimelineStore.getState();
    const message: ExpertAssistMessage = {
        id: `sessionSummary_${crypto.randomUUID()}`,
        isUnread: false,
        text: summary,
        type: "SessionSummary",
        status: "active",
        metadata: {
            sessionSummary: {
                header,
                summary,
                timestamp,
                resolutionStatus,
                sessionType,
                previousSessionId,
            },
        },
    };
    addMessage(sessionId, message);
};

export const addGuideRecommendationMessage = (guideRecommendation: string, sessionId: string) => {
    const { addMessage } = useTimelineStore.getState();
    const message: ExpertAssistMessage = {
        id: `guidedFlowRecommendation_${crypto.randomUUID()}`,
        isUnread: false,
        status: "active",
        text: "Launching guided flow...",
        type: "GuideRecommendation",
        metadata: {
            guideRecommendation,
        },
    };
    addMessage(sessionId, message);
};

export const addGenericMessage = (sessionId: string, text: string) => {
    const { addMessage } = useTimelineStore.getState();
    const message: ExpertAssistMessage = {
        id: `genericMessage_${crypto.randomUUID()}`,
        isUnread: false,
        status: "active",
        text,
        type: "Generic",
    };
    addMessage(sessionId, message);
};

export const addChatSmartOfferMessage = (sessionId: string, text: string, subType: MessageSubType, tag?: string) => {
    const { addMessage } = useTimelineStore.getState();
    const message: ExpertAssistMessage = {
        id: `chatSmartOfferMessage_${crypto.randomUUID()}`,
        isUnread: false,
        status: "active",
        text,
        type: "SmartOffer",
        subType,
        tag,
    };
    addMessage(sessionId, message);
};

export const addChatSmartOfferResponseMessage = (sessionId: string, text: string) => {
    const { addMessage } = useTimelineStore.getState();
    const message: ExpertAssistMessage = {
        id: `chatSmartOfferResponseMessage_${crypto.randomUUID()}`,
        isUnread: false,
        status: "active",
        text,
        type: "SmartOfferResponse",
    };
    addMessage(sessionId, message);
};

export const addChatSmartOfferGenericMessage = (sessionId: string, text: string) => {
    const { addMessage } = useTimelineStore.getState();
    const message: ExpertAssistMessage = {
        id: `chatSmartOfferGenericMessage_${crypto.randomUUID()}`,
        isUnread: false,
        status: "active",
        text,
        type: "SmartOfferGeneric",
    };
    addMessage(sessionId, message);
};
